import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Event {
  id: string
  title: string
  brand: string
  description: string
  date: string
  time: string
  address: string
  boxesLeft: number
  totalBoxes: number
  theme: string
  type: 'walk-in' | 'reservation'
  requiredPoints: number
  image?: string
}

export interface User {
  phone: string
  name: string
  ageRange?: string
  city?: string
  interests?: string[]
  storePreference?: string
  purchaseFrequency?: string
  lifestyle?: string
  points: number
  level: 'Beginner' | 'Fisher' | 'Pro' | 'Master'
  eligible_for_next_event: boolean
}

export interface RegisteredUser extends User {
  registeredAt: number
  occupation?: string
}

export interface Reservation {
  id: string
  eventId: string
  userId: string
  status: 'reserved' | 'redeemed' | 'expired' | 'reviewed'
  timestamp: number
  userCode: string
  staffCode: string
}

export interface ReviewEntry {
  id: string
  reservationId: string
  userId: string
  productName: string
  productBrand: string
  taste: boolean | null
  packaging: boolean | null
  buyAgain: boolean | null
  timestamp: number
}

interface AppState {
  user: User | null
  registeredUsers: RegisteredUser[]
  events: Event[]
  reservations: Reservation[]
  reviews: ReviewEntry[]
  activeEventId: string | null
  setUser: (user: User) => void
  updateUser: (updates: Partial<User>) => void
  setActiveEventId: (id: string | null) => void
  addReservation: (eventId: string, status?: 'reserved' | 'redeemed') => void
  redeemReservation: (reservationId: string) => void
  completeReview: (reservationId: string) => void
  addReviewEntry: (entry: Omit<ReviewEntry, 'id' | 'timestamp'>) => void
  resetStore: () => void
}

const generateCode = (length: number) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

const DEFAULT_EVENTS: Event[] = [
  {
    id: '1',
    title: 'Summer Refresh Drop',
    brand: 'NOCCO',
    description: 'Get the new Limón flavor before anyone else!',
    date: '2026-06-15',
    time: '12:00 - 14:00',
    address: 'Kungsträdgården, Stockholm',
    boxesLeft: 243,
    totalBoxes: 500,
    theme: 'Summer Vibes',
    type: 'walk-in',
    requiredPoints: 0,
  },
  {
    id: '2',
    title: 'Protein Power Hour',
    brand: 'Barebells',
    description: 'Fuel your workout with our new soft bars.',
    date: '2026-06-16',
    time: '16:00 - 18:00',
    address: 'Odenplan, Stockholm',
    boxesLeft: 12,
    totalBoxes: 300,
    theme: 'Fitness',
    type: 'reservation',
    requiredPoints: 50,
  },
  {
    id: '3',
    title: 'Snack Attack',
    brand: 'Estrella',
    description: 'Try the new spicy chili chips.',
    date: '2026-06-20',
    time: '11:00 - 15:00',
    address: 'Medborgarplatsen, Stockholm',
    boxesLeft: 450,
    totalBoxes: 500,
    theme: 'Snacks',
    type: 'reservation',
    requiredPoints: 100,
  },
]

const INITIAL_STATE = {
  user: null,
  registeredUsers: [] as RegisteredUser[],
  activeEventId: '1',
  events: DEFAULT_EVENTS,
  reservations: [] as Reservation[],
  reviews: [] as ReviewEntry[],
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      setUser: (user) =>
        set((state) => ({
          user,
          registeredUsers: [
            ...state.registeredUsers,
            { ...user, registeredAt: Date.now() },
          ],
        })),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      setActiveEventId: (id) => set({ activeEventId: id }),
      addReservation: (eventId, status = 'reserved') =>
        set((state) => ({
          reservations: [
            ...state.reservations,
            {
              id: Math.random().toString(36).substr(2, 9),
              eventId,
              userId: state.user?.phone || 'unknown',
              status: status,
              timestamp: Date.now(),
              userCode: generateCode(4),
              staffCode: generateCode(4),
            },
          ],
          events: state.events.map((e) =>
            e.id === eventId ? { ...e, boxesLeft: e.boxesLeft - 1 } : e,
          ),
          user: state.user
            ? {
                ...state.user,
                points: state.user.points + (status === 'redeemed' ? 20 : 10),
              }
            : null,
        })),
      redeemReservation: (reservationId) =>
        set((state) => ({
          reservations: state.reservations.map((r) =>
            r.id === reservationId ? { ...r, status: 'redeemed' } : r,
          ),
          user: state.user
            ? { ...state.user, points: state.user.points + 20 }
            : null,
        })),
      completeReview: (reservationId) =>
        set((state) => ({
          reservations: state.reservations.map((r) =>
            r.id === reservationId ? { ...r, status: 'reviewed' } : r,
          ),
          user: state.user
            ? {
                ...state.user,
                points: state.user.points + 30,
                eligible_for_next_event: true,
              }
            : null,
        })),
      addReviewEntry: (entry) =>
        set((state) => ({
          reviews: [
            ...state.reviews,
            {
              ...entry,
              id: Math.random().toString(36).substr(2, 9),
              timestamp: Date.now(),
            },
          ],
        })),
      resetStore: () => set(INITIAL_STATE),
    }),
    {
      name: 'finns-i-sjon-storage',
      version: 1,
    },
  ),
)
